import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Agreement, fetchAgreements } from "@/networking/agreements.api";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";

export default function OptionalAgreements({
  agreements,
  onCheckedChange,
}: {
  agreements: Agreement[];
  onCheckedChange?: (checkedIds: number[]) => void;
}) {
  const { t } = useTranslation();

  const [checkedState, setCheckedState] = useState<Record<number, boolean>>(
    () =>
      agreements.reduce(
        (acc, agreement) => ({
          ...acc,
          [agreement.id]: agreement.default_checked,
        }),
        {} as Record<number, boolean>,
      ),
  );

  const fetchAgreementMutation = useMutation({
    mutationFn: (id: string) => fetchAgreements(id),
    onError: (error) => {
      console.error("Error fetching agreement", error);
    },
  });

  const agreementData = fetchAgreementMutation.data;
  const isLoading = fetchAgreementMutation.isPending;

  // Notify parent whenever checked state changes (including default-checked on mount)
  useEffect(() => {
    const checkedIds = Object.entries(checkedState)
      .filter(([, checked]) => checked)
      .map(([id]) => Number(id));
    onCheckedChange?.(checkedIds);
  }, [checkedState, onCheckedChange]);

  const handleToggle = (agreementId: number) => {
    setCheckedState((prev) => ({ ...prev, [agreementId]: !prev[agreementId] }));
  };

  return (
    <div className="relative z-20 mt-4 space-y-2">
      {agreements.map((agreement) => (
        <div key={agreement.id} className="flex flex-row items-center">
          <div className="flex h-5 items-center">
            <input
              id={`optional-agreement-${agreement.id}`}
              type="checkbox"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-black focus:ring-black"
              checked={checkedState[agreement.id] ?? false}
              onChange={() => handleToggle(agreement.id)}
            />
          </div>
          <div className="ml-3 text-xs">
            <label
              htmlFor={`optional-agreement-${agreement.id}`}
              className="cursor-pointer font-medium text-gray-700"
            >
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    className="text-xs text-gray-500 underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchAgreementMutation.mutate(agreement.id?.toString());
                    }}
                  >
                    {agreement.title}
                  </button>
                </DialogTrigger>
                <DialogContent className="z-50 h-[80vh] overflow-y-auto sm:max-w-[600px]">
                  <DialogHeader>
                    <DialogTitle>{agreementData?.agreement.title}</DialogTitle>
                    {isLoading ? (
                      <div className="flex h-full items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-t-2 border-t-gray-500 border-r-gray-500" />
                      </div>
                    ) : (
                      <iframe
                        srcDoc={agreementData?.agreement.description || ""}
                        className="h-full w-full border-none"
                        title={t("agreement_content")}
                      />
                    )}
                  </DialogHeader>
                </DialogContent>
              </Dialog>
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}
